export type MitsubishiTargetProfile = 'detail' | 'whole';

export const MITSUBISHI_TARGET_MIN_CELSIUS = -99;
export const MITSUBISHI_TARGET_MAX_CELSIUS = 100;

// Captured AE-C400 control asset: main79a190797b4bf24a0604.js (2026-09-20).
// SHA-256: f86a5bf426d3ad11e328fb75c68f282eb4f6efa6158999c8f4627f37aa4ca7db
// CI at offset 3699754, LI at 3704825, NI/AI at 3710128/3710234,
// yI/DI at 3710704/3710986; cx/ux repeat these target rules.
// These are setpoint rules. The general converter selects the opposite table
// for its boolean flag and must not be used to convert target temperatures.
// CI keys run from -99 C through 100 C in 0.5 C increments. LI differs only
// at 20 C, where its Fahrenheit value is 67 rather than 68.
const detailedFahrenheit = [
  -146, -145, -144, -144, -143, -142, -141, -140, -139, -138, -137, -136, -135,
  -135, -134, -133, -132, -131, -130, -129, -128, -127, -126, -126, -125, -124,
  -123, -122, -121, -120, -119, -118, -117, -117, -116, -115, -114, -113, -112,
  -111, -110, -109, -108, -108, -107, -106, -105, -104, -103, -102, -101, -100,
  -99, -99, -98, -97, -96, -95, -94, -93, -92, -91, -90, -90, -89, -88, -87,
  -86, -85, -84, -83, -82, -81, -81, -80, -79, -78, -77, -76, -75, -74, -73,
  -72, -72, -71, -70, -69, -68, -67, -66, -65, -64, -63, -63, -62, -61, -60,
  -59, -58, -57, -56, -55, -54, -54, -53, -52, -51, -50, -49, -48, -47, -46,
  -45, -45, -44, -43, -42, -41, -40, -39, -38, -37, -36, -36, -35, -34, -33,
  -32, -31, -30, -29, -28, -27, -27, -26, -25, -24, -23, -22, -21, -20, -19,
  -18, -18, -17, -16, -15, -14, -13, -12, -11, -10, -9, -9, -8, -7, -6, -5, -4,
  -3, -2, -1, 0, 1, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 10, 11, 12, 13, 14, 15, 16,
  17, 18, 19, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 28, 29, 30, 31, 32, 33,
  34, 35, 36, 37, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 46, 47, 48, 49, 50,
  51, 52, 53, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 67,
  68, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85,
  86, 87, 88, 88, 89, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 100, 101,
  102, 103, 104, 105, 106, 107, 108, 109, 109, 110, 111, 112, 113, 114, 115,
  116, 117, 118, 118, 119, 120, 121, 122, 123, 124, 125, 126, 127, 127, 128,
  129, 130, 131, 132, 133, 134, 135, 136, 136, 137, 138, 139, 140, 141, 142,
  143, 144, 145, 145, 146, 147, 148, 149, 150, 151, 152, 153, 154, 154, 155,
  156, 157, 158, 159, 160, 161, 162, 163, 163, 164, 165, 166, 167, 168, 169,
  170, 171, 172, 172, 173, 174, 175, 176, 177, 178, 179, 180, 181, 181, 182,
  183, 184, 185, 186, 187, 188, 189, 190, 190, 191, 192, 193, 194, 195, 196,
  197, 198, 199, 199, 199, 199, 199, 199, 199, 199, 199, 199, 199, 199, 199,
  199, 199, 199,
] as const;

const isKnownProfile = (
  profile: MitsubishiTargetProfile,
): profile is MitsubishiTargetProfile =>
  profile === 'detail' || profile === 'whole';

const tableFahrenheit = (
  celsius: number,
  profile: MitsubishiTargetProfile,
): number | undefined => {
  if (
    !Number.isFinite(celsius) ||
    celsius < MITSUBISHI_TARGET_MIN_CELSIUS ||
    celsius > MITSUBISHI_TARGET_MAX_CELSIUS ||
    !Number.isInteger(celsius * 2)
  ) {
    return undefined;
  }
  if (profile === 'whole' && celsius === 20) return 67;
  return detailedFahrenheit[(celsius - MITSUBISHI_TARGET_MIN_CELSIUS) * 2];
};

/**
 * Resolve an explicitly edited Fahrenheit target using the captured DI rule.
 * Callers must still enforce the device's native grid, ranges and permissions.
 * Never use this inverse to reconstruct an unchanged canonical Celsius target:
 * multiple native values can share one Fahrenheit label.
 */
export const mitsubishiTargetCelsius = (
  fahrenheit: number,
  profile: MitsubishiTargetProfile,
): number | undefined => {
  if (!isKnownProfile(profile) || !Number.isInteger(fahrenheit))
    return undefined;

  // AI chooses the last Celsius candidate in the source table, then DI applies
  // the special target preferences around 19 C and 20 C.
  for (let index = detailedFahrenheit.length - 1; index >= 0; index -= 1) {
    const celsius = MITSUBISHI_TARGET_MIN_CELSIUS + index / 2;
    if (tableFahrenheit(celsius, profile) !== fahrenheit) continue;
    if (profile === 'detail' && (celsius === 19.5 || celsius === 20.5)) {
      return celsius - 0.5;
    }
    if (profile === 'whole' && celsius === 20) return 19;
    return celsius;
  }
  return undefined;
};

/**
 * Produce the captured yI/cx target label without changing canonical Celsius.
 * Unknown profiles, unsupported native values and nonfinite values have no
 * qualified vendor label; the caller decides its presentation fallback.
 */
export const mitsubishiTargetFahrenheit = (
  celsius: number,
  profile: MitsubishiTargetProfile,
): number | undefined => {
  if (!isKnownProfile(profile)) return undefined;
  const fahrenheit = tableFahrenheit(celsius, profile);
  if (fahrenheit === undefined) return undefined;
  const nativeTarget = mitsubishiTargetCelsius(fahrenheit, profile);
  if (nativeTarget === undefined) return undefined;
  // Unlike bare NI, actual whole-profile target display passes through DI,
  // floors its result in Celsius, and looks up Fahrenheit again.
  return tableFahrenheit(
    profile === 'whole' ? Math.floor(nativeTarget) : nativeTarget,
    profile,
  );
};
